import { createClient } from '@supabase/supabase-js';
import type { AuthAdapter, OAuthProvider, SignInCredentials, SignUpCredentials } from '../types';
import type { AuthTokens, SignUpResult } from '@/core/types/auth.types';
import { env } from '@/core/config/env';

const supabase = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY);

const PROVIDER_MAP: Record<OAuthProvider, 'google' | 'azure' | 'apple'> = {
  google: 'google',
  microsoft: 'azure',
  apple: 'apple',
};

type SupabaseSession = NonNullable<
  Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']
>;

function mapTokens(session: SupabaseSession): AuthTokens {
  return {
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    accessTokenExpiresAt: session.expires_at
      ? session.expires_at * 1000
      : Date.now() + session.expires_in * 1000,
  };
}

export const supabaseAdapter: AuthAdapter = {
  async signInWithPassword({ email, password }: SignInCredentials): Promise<AuthTokens> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.session) throw new Error(error?.message ?? 'Error al iniciar sesión');
    return mapTokens(data.session);
  },

  async signUp({ email, password, name }: SignUpCredentials): Promise<SignUpResult> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw new Error(error.message);
    // With email confirmation enabled, Supabase creates the user but returns no
    // session until the user clicks the confirmation link. The link redirects to
    // /auth/callback, where useAuthInit picks up the SIGNED_IN event.
    if (!data.session) return { status: 'pending_confirmation' };
    return { status: 'active', tokens: mapTokens(data.session) };
  },

  async getSession(): Promise<AuthTokens | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) return null;
    return mapTokens(data.session);
  },

  async loginWithProvider(provider: OAuthProvider): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: PROVIDER_MAP[provider],
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw new Error(error.message);
  },

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  },

  onSessionChange(callback: (tokens: AuthTokens | null) => void): () => void {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      switch (event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
          callback(session ? mapTokens(session) : null);
          break;
        case 'SIGNED_OUT':
          callback(null);
          break;
        default:
          break;
      }
    });

    return () => subscription.unsubscribe();
  },

  async refreshSession(): Promise<AuthTokens> {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) throw new Error(error?.message ?? 'Error al refrescar la sesión');
    return mapTokens(data.session);
  },

  async resetPasswordForEmail(email: string): Promise<void> {
    // Sin redirectTo: la plantilla de "Reset Password" en Supabase envía el
    // código {{ .Token }} (OTP), no un enlace — el flujo se completa dentro
    // de la app vía verifyRecoveryOtp, nunca por /auth/callback.
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw new Error(error.message);
  },

  async verifyRecoveryOtp(email: string, code: string): Promise<AuthTokens> {
    const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' });
    if (error || !data.session) throw new Error(error?.message ?? 'Código inválido o expirado');
    return mapTokens(data.session);
  },

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  },
};
